import ReactEcharts from 'echarts-for-react';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import {
  formatChartLabelValueForSelectedMeasure,
  formatChartXAxisValue,
  formatChartYAxisValue,
} from '../ChartOptions/Format/NumberFormatter';
import {
  ChartsMapStateToProps,
  ChartsReduxStateProps,
  FormatterValueProps,
} from './ChartsCommonInterfaces';

import { palette } from '../..';

import {
  checkUidPresenceInCheckedMeasureForTooltip,
  displayName,
  limitChartData,
  shouldChartDisplayWithSecurity,
  applyOpacityToColor,
} from '../CommonFunctions/CommonFunctions';
import EmptyQuery from './EmptyQuery';
import { RootState } from '../../redux';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import { tooltipStyle } from '../Common/EChartTooltip';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import {
  AXIS_THRESHOLDS,
  LABEL_THRESHOLDS,
  LEGEND_THRESHOLDS,
  MARGIN_THRESHOLDS,
} from '../ChartOptions/ChartThresholds';

const ScatterChart = ({
  //props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  fontFamily,
  softUI,

  //state
  chartControls,
  chartProperties,
}: ChartsReduxStateProps) => {
  var chartControl: ChartControlsProps = chartControls.properties[propKey];
  const {
    tooltipFormats,
    chartAxes,
    isTooltipEnabled,
    chartType,
    tabState,
    selectedTab,
    showDash,
    dateFormats,
    selectedMeasureName,
  } = useSelector((state: RootState) => ({
    tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
    chartAxes: state.chartProperties?.properties[propKey]?.chartAxes,
    isTooltipEnabled:
      state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
    chartType: state.chartProperties?.properties[propKey]?.chartType,
    tabState: state.tabState,
    selectedTab: state.tabTileProps.selectedTabId,
    showDash: state.tabTileProps.showDash,
    dateFormats: state.chartControls?.properties[propKey]?.formatOptions?.dateFormats,
    selectedMeasureName:
      state.chartControls?.properties[propKey]?.formatOptions?.dateFormats?.selectedMeasureName,
  }));

  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];

  const [chartDataKeys, setChartDataKeys] = useState<string[]>([]);
  const scatterControls = chartControl.scatterPlotChartControls || {};
  const {
    manualColor,
    nodeValuesColors,
    allSeriesColor,
    opacity,
    enableBubbleBorder,
    bubbleSize,
    bubbleBorderWidth,
  } = scatterControls;
  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;
  const limitedChartData = limitChartData(chartData, 5000, chartAxes[2]?.fields[0]?.displayname);

  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (chartData.length >= 1) {
      if (typeof chartData === 'object' && chartData.length > 0) {
        setChartDataKeys(Object.keys(chartData[0]));
      }
    }
  }, [chartData]);

  useEffect(() => {
    if (chartData.length > 5000 && !showDash) {
      toast.warning(`${messages.chart.excessiveData} 5000 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  const processedChartData = limitedChartData.map((item) => {
    return Object.fromEntries(
      Object.entries(item).map(([key, value]) => [
        key,
        value === null
          ? '(Blank)'
          : typeof value === 'boolean'
            ? value
              ? 'True'
              : 'False'
            : value,
      ]),
    );
  });

  // --- Clamping Helper ---
  const getClampedValue = (val: any, type: 'x' | 'y') => {
    if (val === null || val === undefined || isNaN(Number(val))) return val;
    let finalVal = Number(val);
    const minMaxSettings = chartControl.axisOptions?.scatterChartMinMax || {};

    if (type === 'x') {
      // Clamping Logic for X Axis
      if (
        minMaxSettings.x_enableMax &&
        minMaxSettings.x_maxValue !== null &&
        finalVal > minMaxSettings.x_maxValue
      ) {
        finalVal = minMaxSettings.x_maxValue;
      }
      if (
        minMaxSettings.x_enableMin &&
        minMaxSettings.x_minValue !== null &&
        finalVal < minMaxSettings.x_minValue
      ) {
        finalVal = minMaxSettings.x_minValue;
      }
    } else {
      // Clamping Logic for Y Axis
      if (
        minMaxSettings.y_enableMax &&
        minMaxSettings.y_maxValue !== null &&
        finalVal > minMaxSettings.y_maxValue
      ) {
        finalVal = minMaxSettings.y_maxValue;
      }
      if (
        minMaxSettings.y_enableMin &&
        minMaxSettings.y_minValue !== null &&
        finalVal < minMaxSettings.y_minValue
      ) {
        finalVal = minMaxSettings.y_minValue;
      }
    }
    return finalVal;
  };

  //  series data
  const getSeriesData = () => {
    if (chartData.length < 1) return [];

    var keys = Object.keys(chartData[0]);
    // Assuming keys[0]=Tooltip/Name, keys[1]=X-Axis, keys[2]=Y-Axis based on standard format
    const nameKey = keys[0];
    const xKey = keys[1];
    const yKey = keys[2];

    let legendColor = defaultThemeColor;
    if (manualColor && allSeriesColor) {
      legendColor = allSeriesColor;
    }

    const dataPoints = processedChartData.map((item) => {
      const rawX = item[xKey];
      const rawY = item[yKey];

      const clampedX = getClampedValue(rawX, 'x');
      const clampedY = getClampedValue(rawY, 'y');

      let pointColor = defaultThemeColor;
      const val = String(item[nameKey]);

      if (manualColor) {
        if (nodeValuesColors && nodeValuesColors[val]) {
          pointColor = nodeValuesColors[val];
        } else if (allSeriesColor) {
          pointColor = allSeriesColor;
        }
      }

      const fillOpacity = (opacity !== undefined ? opacity : 50) / 100;

      return {
        // ECharts Scatter value format: [x, y, ...extras]
        value: [clampedX, clampedY],
        // Store original item for tooltip/label
        originalItem: item,
        name: item[nameKey],
        itemStyle: {
          color: applyOpacityToColor(pointColor, fillOpacity),
          borderColor: enableBubbleBorder ? pointColor : undefined,
          borderWidth: enableBubbleBorder ? bubbleBorderWidth : 0,
          opacity: 1,
        },
      };
    });

    var seriesObj: any = {
      symbolSize: bubbleSize ? bubbleSize : chartArea === 'dashboard' ? 10 : 20,
      type: 'scatter',
      data: dataPoints,
      name: nameKey,
      itemStyle: {
        color: legendColor,
        ...(softUI
          ? {
              shadowColor: 'rgba(0, 0, 0, 0.5)',
              shadowBlur: 10,
              shadowOffsetX: 3,
              shadowOffsetY: 3,
            }
          : {}),
      },
      emphasis: {
        focus: 'self',
      },
      blur: {
        itemStyle: {
          opacity: 0.5,
        },
      },
      label: {
        show:
          Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
            ? chartControl.labelOptions.showLabel
            : false,
        fontSize: chartControl.labelOptions.fontSize,
        fontFamily: fontFamily,
        color: chartControl.labelOptions.labelColor
          ? chartControl.labelOptions.labelColor
          : selectedTheme?.dark
            ? '#ffffff'
            : '#757474',
        backgroundColor: chartControl.labelOptions.labelBackground
          ? chartControl.labelOptions.labelBackgroundColor
          : 'transparent',
        borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
        padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
        position: [10, -10],
        formatter: (params: any) => {
          // Retrieve original value from data object
          const item = params.data?.originalItem;
          const dimensionField = chartProperties.properties[propKey].chartAxes[1].fields[0];
          const dimensionName = dimensionField?.displayname;

          const dimensionUid = dimensionField?.uId;

          if (dimensionName && item) {
            var rawDimensionValue = item[dimensionName];

            const labelFormat = chartControl?.formatOptions?.labelFormats?.measureFormats?.[
              dimensionUid
            ] as any;

            var formattedValue = formatChartLabelValueForSelectedMeasure(
              chartControls.properties[propKey],
              chartProperties.properties[propKey],
              rawDimensionValue,
              dimensionName,
              labelFormat,
            );

            return formattedValue;
          }
          return '';
        },
      },
    };

    return [seriesObj];
  };

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (!chartControl.colorScheme || chartArea === 'dashboard') {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];
  const defaultThemeColor = selectedTheme?.colors[0] || '#5470c6';

  const getTopMarginForLegend = () => {
    var top = '';
    if (chartControl.legendOptions?.position?.top === 'top') {
      top = 'top';
      // } else if (chartControl.legendOptions?.position?.top === "bottom") {
      //   top = "90%";
    } else {
      top = '50%';
    }
    return top;
  };

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !mouseOverInteractivity ||
        (tabState.tabs[selectedTab].hoveredPropkey &&
          tabState.tabs[selectedTab].hoveredPropkey === propKey)
      ) {
        return;
      }
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      const index = processedChartData.findIndex((item) =>
        tabState.tabs[selectedTab].hoveredItems?.some(
          (hovered: Record<string, any>) =>
            String(hovered[chartDataKeys[0]]) === String(item[chartDataKeys[0]]),
        ),
      );

      if (index >= 0) {
        chartInstance.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: index,
        });
      } else {
        chartInstance.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
        });
      }
    }, 30); // slight delay helps when charts update

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);
  const hoveredRef = useRef<Record<string, any> | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;

    chartInstance.on('mouseover', (params: any) => {
      if (
        params?.seriesName &&
        hoveredRef.current?.[params.seriesName] !== params.data[params.seriesName]
      ) {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

        hoverdTimeout.current = setTimeout(() => {
          const newEntry = { [params.seriesName]: params.data[params.seriesName] };
          hoveredRef.current = newEntry;

          setHoveredColumns((prev) => {
            const isDuplicate = prev.some(
              (item) => item[params.seriesName] === params.data[params.seriesName],
            );
            if (!isDuplicate) {
              return [...prev, newEntry];
            }
            return prev;
          });
        }, 200);
      }
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.seriesName) {
        hoveredRef.current = null;
        setHoveredColumns([]);
      }
    });
  };

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const chartInstance = chartRef.current?.getEchartsInstance?.();
  const currentOption = chartInstance?.getOption();
  const [currentLegendSelected, setCurrentLegendSelected] = useState<any>(
    currentOption?.legend?.[0]?.selected ?? {},
  );

  const onLegendSelectChanged = (params: any) => {
    setCurrentLegendSelected(params.selected);
  };

  const RenderChart = () => {
    return (
      <>
        <ReactEcharts
          ref={chartRef}
          onChartReady={handleChartReady}
          onEvents={{
            legendselectchanged: onLegendSelectChanged,
          }}
          // theme={chartControl.colorScheme}
          style={{
            // padding: "1rem",
            width: graphDimension.width,
            height: graphDimension.height,
            margin: 'auto',
            border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
          }}
          option={{
            color: chartThemes[0]?.colors || [],
            backgroundColor: chartThemes[0]?.background || [],
            // animation: chartArea ? false : true, // Turning animation off for current release, to be added later on
            animation: false,
            legend: {
              // textStyle :{color : getContrastColor(chartThemes[0].background)},
              type: 'scroll',
              show:
                Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.scatter.h
                  ? chartControl.legendOptions?.showLegend
                  : false,
              itemHeight: chartControl.legendOptions?.symbolHeight,
              itemWidth: chartControl.legendOptions?.symbolWidth,
              itemGap: chartControl.legendOptions?.itemGap,

              left: chartControl.legendOptions?.position?.left,
              top:
                chartControl.legendOptions?.position?.top !== 'bottom'
                  ? getTopMarginForLegend()
                  : null,
              // top: chartControl.legendOptions?.position?.top,
              bottom: chartControl.legendOptions?.position?.top === 'bottom' ? 0 : null,
              orient: chartControl.legendOptions?.orientation,
              selected: currentLegendSelected,
              textStyle: {
                color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
                fontFamily: fontFamily,
              },
            },
            grid: {
              left:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.chartMargin.left + 5 + '%',
              right:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.chartMargin.right + '%',
              top:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.legendOptions?.position?.top === 'top'
                    ? chartControl.chartMargin.top + 5 + '%'
                    : chartControl.chartMargin.top + '%',
              bottom:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.legendOptions?.position?.top === 'bottom'
                    ? (graphDimension.height * chartControl.chartMargin.bottom) / 100 + 35
                    : chartControl.chartMargin.bottom + '%',
              shadowColor: 'rgba(0, 0, 0, 0.3)', // Shadow color for grid
              shadowBlur: 5, // Shadow blur for grid
              shadowOffsetX: 2, // Horizontal shadow offset for grid
              shadowOffsetY: 2, // Vertical shadow offset for grid
            },
            tooltip: {
              show: isTooltipEnabled,
              useHTML: true,
              trigger: 'item',
              backgroundColor: 'white',
              borderWidth: 1,
              padding: 0,
              textStyle: {
                color: '#333',
                fontSize: 16,
              },
              appendToBody: true,
              extraCssText:
                'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
              formatter: function (params: any) {
                const tempData = params.data?.originalItem;
                if (!tempData) return '';
                const dimensionKey = chartAxes[1]?.fields[0]?.displayname;
                const dimensionValue = tempData[dimensionKey as string];
                //----------remove unchecked measure or any other field value of type date inside tooltip----------//
                const _data: { [key: string]: string | number } = {};
                Object.keys(tempData).forEach((key: string) => {
                  const resp = checkUidPresenceInCheckedMeasureForTooltip(
                    chartAxes,
                    key,
                    tooltipFormats?.checkedMeasure,
                  );
                  if (resp) {
                    _data[key] = tempData[key];
                  }
                });
                let mergedData = { ..._data };
                //-- send extra responses in _data so that it maps and shows all key value pairs including extra measure (if any)--//
                //----get extra response corresponding to a particular dimension----//
                const extraResponses = tooltipFormats.extraMeasureFieldsResponses;
                if (tooltipFormats && extraResponses.length > 0) {
                  const extraFieldObj = extraResponses.find(
                    (obj: any) =>
                      String(obj[dimensionKey as string]) === String(dimensionValue as string),
                  );
                  if (extraFieldObj) {
                    //---- Remove all dimension keys from extraFieldObj ----//
                    const { [dimensionKey as string]: _, ...restExtraFields } = extraFieldObj;
                    //-----remove unchecked measure field after adding extra measures ----//
                    let filteredFields = { ...mergedData, ...restExtraFields };
                    const removeUncheckedAxisMeasure = (axisName: string) => {
                      const measureKey = chartAxes.find((axis: any) => axis.name === axisName)
                        ?.fields[0]?.displayname;
                      if (measureKey) {
                        const isPresent = checkUidPresenceInCheckedMeasureForTooltip(
                          chartAxes,
                          measureKey,
                          tooltipFormats?.checkedMeasure,
                        );
                        if (!isPresent) {
                          delete filteredFields[measureKey];
                        }
                      }
                    };
                    removeUncheckedAxisMeasure('X Axis');
                    removeUncheckedAxisMeasure('Y Axis');
                    //send final response in tooltip data i.e checked measure plus extra fields
                    mergedData = { ...filteredFields };
                  }
                }
                return tooltipStyle(
                  params.color,
                  mergedData,
                  tooltipFormats,
                  chartAxes,
                  dimensionKey,
                  undefined,
                  undefined,
                  fontFamily,
                );
              },
            },
            dataset: {
              dimensions: Object.keys(processedChartData[0]),
              source: processedChartData,
            },
            xAxis: {
              position: chartControl.axisOptions.xAxis[1].position,
              min: null,

              // min: chartControl.axisOptions?.scatterChartMinMax?.x_enableMin
              //   ? chartControl.axisOptions.scatterChartMinMax?.x_minValue
              //   : null,
              max: chartControl.axisOptions?.scatterChartMinMax?.x_enableMax
                ? chartControl.axisOptions.scatterChartMinMax?.x_maxValue
                : null,

              axisLine: {
                show:
                  chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                onZero:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? false
                    : chartControl.axisOptions.xAxis[1].onZero,
              },

              show: true,

              name:
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
                  ? chartControl.axisOptions.xAxis[1].name
                  : '',
              nameLocation: chartControl.axisOptions.xAxis[1].nameLocation,
              nameGap: chartControl.axisOptions.xAxis[1].nameGap,
              nameTextStyle: {
                fontSize: chartControl.axisOptions.xAxis[1].nameSize,
                color: chartControl.axisOptions.xAxis[1].nameColor,
              },

              axisTick: {
                show:
                  chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                alignWithLabel: true,
                length:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? chartControl.axisOptions.xAxis[1].tickSizeTop
                    : chartControl.axisOptions.xAxis[1].tickSizeBottom,
              },
              axisLabel: {
                show:
                  chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                fontSize: chartControl.axisOptions.xAxis[1].fontSize,
                rotate:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? chartControl.axisOptions.xAxis[1].tickRotationTop
                    : chartControl.axisOptions.xAxis[1].tickRotationBottom,
                margin:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? chartControl.axisOptions.xAxis[1].tickPaddingTop
                    : chartControl.axisOptions.xAxis[1].tickPaddingBottom,
                color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
                fontFamily: fontFamily,
                formatter: (value: number) => {
                  var formattedValue = formatChartXAxisValue(chartControl, value);
                  return formattedValue;
                },
                // formatter: (value: FormatterValueProps) => {
                //   const columnName = displayName(
                //     chartProperties.properties[propKey].chartAxes[2].fields[0],
                //   );
                //   if (chartDataKeys) {
                //     var formattedValue = value.value[columnName];

                //     formattedValue = formatChartLabelValueForSelectedMeasure(
                //       chartControls.properties[propKey],
                //       chartProperties.properties[propKey],
                //       formattedValue,
                //       columnName,
                //     );

                //     return formattedValue;
                //   }
                // },
              },
              splitLine: {
                show: chartControl.axisOptions.xSplitLine ?? false,
                lineStyle: {
                  color: chartControl.axisOptions.xAxis[1]?.splitLineColor || '#d3d3d3',
                  width: chartControl.axisOptions.xAxis[1]?.splitLineWidth ?? 1,
                  type: chartControl.axisOptions.xAxis[1]?.splitLineType || 'solid',
                },
              },
            },
            yAxis: {
              position:
                chartControl.axisOptions.yAxis[1].position === 'right'
                  ? 'right'
                  : chartControl.axisOptions.yAxis[1].position === 'left'
                    ? 'left'
                    : 'left',

              axisLine: {
                show:
                  chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                onZero:
                  chartControl.axisOptions.yAxis[1].position === 'right'
                    ? false
                    : chartControl.axisOptions.yAxis[1].onZero,
              },
              min: null,

              // min: chartControl.axisOptions.scatterChartMinMax.y_enableMin
              //   ? chartControl.axisOptions.scatterChartMinMax.y_minValue
              //   : null,
              max: chartControl.axisOptions.scatterChartMinMax.y_enableMax
                ? chartControl.axisOptions.scatterChartMinMax.y_maxValue
                : null,

              axisTick: {
                show:
                  chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                alignWithLabel: true,
                length:
                  chartControl.axisOptions.yAxis[1].position === 'left'
                    ? chartControl.axisOptions.yAxis[1].tickSizeLeft
                    : chartControl.axisOptions.yAxis[1].tickSizeRight,
              },

              show: true,

              name:
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
                  ? chartControl.axisOptions.yAxis[1].name
                  : '',
              nameLocation: chartControl.axisOptions.yAxis[1].nameLocation,
              nameGap: chartControl.axisOptions.yAxis[1].nameGap,
              nameTextStyle: {
                fontSize: chartControl.axisOptions.yAxis[1].nameSize,
                color: chartControl.axisOptions.yAxis[1].nameColor,
              },

              axisLabel: {
                show:
                  chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                fontSize: chartControl.axisOptions.yAxis[1].fontSize,
                rotate:
                  chartControl.axisOptions.yAxis[1].position === 'left'
                    ? chartControl.axisOptions.yAxis[1].tickRotationLeft
                    : chartControl.axisOptions.yAxis[1].tickRotationRight,
                margin:
                  chartControl.axisOptions.yAxis[1].position === 'left'
                    ? chartControl.axisOptions.yAxis[1].tickPaddingLeft
                    : chartControl.axisOptions.yAxis[1].tickPaddingRight,
                color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
                fontFamily: fontFamily,
                formatter: (value: number) => {
                  var formattedValue = formatChartYAxisValue(chartControl, value);
                  return formattedValue;
                },
              },
              splitLine: {
                show: chartControl.axisOptions.ySplitLine ?? true,
                lineStyle: {
                  color: chartControl.axisOptions.yAxis[1]?.splitLineColor || '#d3d3d3',
                  width: chartControl.axisOptions.yAxis[1]?.splitLineWidth ?? 1,
                  type: chartControl.axisOptions.yAxis[1]?.splitLineType || 'solid',
                },
              },
            },
            series: getSeriesData(),
          }}
        />
      </>
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};

const mapStateToProps = (state: ChartsMapStateToProps, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
  };
};

export default connect(mapStateToProps, null)(ScatterChart);
